import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import { resolvePortfolioWidgetDeviation } from './resolvePortfolioWidgetDeviation';

export type AuditModelDeviationDisplay =
  | { kind: 'empty' }
  | {
      kind: 'deviation';
      deviationCents: number;
      deviationPercentage: number;
    };

/**
 * Maps model Target $ onto the proposed holdings pool so a correct mix
 * is $0 Deviation. Target % is the model/IPS weight (10%, not 9.97%).
 */
export const resolveAuditProposedPoolScale = (
  rows: readonly WeightDiffDetailRow[],
): number => {
  let targetTotal = 0;
  let proposedTotal = 0;

  for (const row of rows) {
    targetTotal += row.targetCents;
    proposedTotal += row.proposedCents;
  }

  if (targetTotal <= 0 || proposedTotal <= 0) {
    return 1;
  }

  return proposedTotal / targetTotal;
};

/** Audit model Target $ (unscaled unless a diagnostic scale is passed). */
export const resolveAuditModelTargetCents = (
  row: WeightDiffDetailRow,
  scale = 1,
): number => row.targetCents * scale;

/**
 * Completed-run Target % — same figure as the live portfolio widget
 * (report/IPS expectedPercentage, e.g. 10.00% not 9.97%).
 */
export const resolveAuditSnapshotTargetPercentage = (
  row: WeightDiffDetailRow,
): number =>
  row.reportExpectedPercentage ??
  (row.modelTargetPct > 0 ? row.modelTargetPct : row.targetPct);

/** Post-optimization gap vs audit model weights (internal diagnostics). */
export const resolveAuditModelDeviationDisplay = (
  row: WeightDiffDetailRow | undefined,
  scale = 1,
): AuditModelDeviationDisplay => {
  if (!row) {
    return { kind: 'empty' };
  }

  const { deviationCents, deviationPercentage } =
    resolvePortfolioWidgetDeviation({
      valueCents: row.proposedCents,
      expectedValueCents: resolveAuditModelTargetCents(row, scale),
    });

  return {
    kind: 'deviation',
    deviationCents,
    deviationPercentage,
  };
};

/**
 * Completed-run cash Deviation $ vs the cash target.
 * % is leftover / portfolio MV so a small cash target does not show 74%.
 */
export const resolveAuditCashDeviationDisplay = ({
  proposedCashCents,
  expectedCashCents,
  portfolioMarketValueCents,
}: {
  proposedCashCents: number;
  expectedCashCents: number;
  portfolioMarketValueCents: number;
}): AuditModelDeviationDisplay => {
  const { deviationCents } = resolvePortfolioWidgetDeviation({
    valueCents: proposedCashCents,
    expectedValueCents: expectedCashCents,
  });

  return {
    kind: 'deviation',
    deviationCents,
    deviationPercentage:
      portfolioMarketValueCents > 0
        ? deviationCents / portfolioMarketValueCents
        : 0,
  };
};
